import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { APP_EVENT_STATUSES, PAYLOAD_FIELDS, VERIFY_SNIPPET, WEBHOOK_HEADERS } from '@/lib/webhookGuide';
import { CodeExample } from './CodeExample';

// Hand-written summary of the "Webhooks" section of info.description in
// src/data/openapi.yaml (the source of truth). The snippet, headers and payload
// fields live in lib/webhookGuide.ts, whose test checks them against the spec.

function C({ children }: { children: ReactNode }) {
  return <code className="text-blue-600">{children}</code>;
}

function Sub({ children }: { children: ReactNode }) {
  return <h4 className="mt-4 mb-1 text-sm font-semibold">{children}</h4>;
}

export function WebhooksGuide({ baseUrl, apiKey }: { baseUrl: string; apiKey: string }) {
  const catchUpCurl = `curl -X GET "${baseUrl}/api/v1/events?after_seq=1234&limit=100" \\
  -H "X-API-Key: ${apiKey}"`;

  return (
    <div className="space-y-2 text-sm text-muted-foreground">
      <p>
        App state changes are recorded as events and POSTed to webhook receivers (<C>/webhook-receivers</C>, or{' '}
        <Link to="/settings/webhooks" className="text-blue-600 hover:underline">
          Settings → Webhooks
        </Link>
        ). Event types: <C>app.status_changed</C>, <C>app.deployed</C>, <C>app.deploy_failed</C>, <C>app.migrated</C>,{' '}
        <C>app.migration_failed</C>, <C>app.domain_switch_started</C>, <C>app.domain_switched</C>,{' '}
        <C>app.domain_switch_failed</C>, <C>app.deleted</C>, <C>domain.ready_changed</C>, plus <C>ping</C> from "Send
        test". A receiver with an empty <C>event_types</C> gets every type. Domain events have no app (<C>app_id</C> is{' '}
        <C>null</C>) and reach every API key, including <C>own_apps_only</C> keys and per-key receivers.
      </p>

      <Sub>Request headers</Sub>
      <p>
        <C>POST &lt;url&gt;</C> with <C>Content-Type: application/json</C>, <C>User-Agent: portainer-bridge-webhooks/1</C> and:
      </p>
      <dl className="divide-y rounded-lg border bg-muted">
        {WEBHOOK_HEADERS.map((h) => (
          <div key={h.name} className="grid gap-1 px-4 py-2 sm:grid-cols-[13rem_minmax(0,1fr)] sm:gap-4">
            <dt className="font-mono text-xs text-foreground">{h.name}</dt>
            <dd>{h.meaning}</dd>
          </div>
        ))}
      </dl>

      <Sub>Payload</Sub>
      <p>
        The body is the event envelope <C>{'{"seq", "id", "type", "app_id", "occurred_at", "data"}'}</C> (see the{' '}
        <C>Event</C> schema). For app events <C>data.app</C> is a snapshot of the app when the event was recorded; env vars
        are never included. Extra <C>data</C> fields by type:
      </p>
      <dl className="divide-y rounded-lg border bg-muted">
        {PAYLOAD_FIELDS.map(({ type, fields }) => (
          <div key={type} className="grid gap-1 px-4 py-2 sm:grid-cols-[13rem_minmax(0,1fr)] sm:gap-4">
            <dt className="font-mono text-xs text-foreground">{type}</dt>
            <dd>
              {fields.length === 0 ? (
                '—'
              ) : (
                <ul className="space-y-0.5">
                  {fields.map((f) => (
                    <li key={f.name}>
                      <C>{f.name}</C>
                      {f.note ? `: ${f.note}` : null}
                    </li>
                  ))}
                </ul>
              )}
            </dd>
          </div>
        ))}
      </dl>
      <p>
        App status values (<C>data.app.status</C>, <C>data.previous_status</C>):{' '}
        {APP_EVENT_STATUSES.map((st, i) => (
          <span key={st}>
            {i > 0 ? ', ' : null}
            <C>{st}</C>
          </span>
        ))}
        . A migration whose cutover fails can leave the app in <C>migrating</C> with only an <C>app.migration_failed</C>{' '}
        event. Free-text <C>error</C> strings may include upstream (Portainer) or internal messages.
      </p>
      <p>
        Domain switches: the app keeps its old hostname until <C>app.domain_switched</C>, whose <C>data.app</C> already
        shows the new <C>full_subdomain</C> and <C>domain</C>; the old hostname keeps serving until the grace period ends.
        After <C>app.domain_switch_failed</C> the app is still on its old hostname.
      </p>

      <Sub>Verifying the signature</Sub>
      <ul className="list-disc space-y-1 pl-5">
        <li>
          The HMAC key is the secret string exactly as returned, including the <C>whsec_</C> prefix, as UTF-8 bytes. Do not
          base64-decode it (unlike Standard Webhooks / Svix).
        </li>
        <li>
          The message is the <C>X-Bridge-Timestamp</C> value, then <C>.</C>, then the raw request body bytes, before any JSON
          parsing.
        </li>
        <li>
          Compare <C>sha256=</C> + lowercase hex of the HMAC with <C>X-Bridge-Signature</C> in constant time.
        </li>
        <li>
          Reject requests whose <C>X-Bridge-Timestamp</C> is more than 5 minutes old.
        </li>
        <li>The secret is returned only by create and rotate-secret.</li>
      </ul>
      <CodeExample code={VERIFY_SNIPPET} language="javascript" />

      <Sub>Delivery semantics</Sub>
      <ul className="list-disc space-y-1 pl-5">
        <li>
          <strong className="font-medium text-foreground">At-least-once.</strong> Any 2xx is success; anything else, including a
          3xx, is a failed attempt. Retries reuse the event id, so dedupe on <C>X-Bridge-Event-Id</C>. Rarely, an outcome event
          (<C>app.deployed</C> / <C>app.deploy_failed</C>) is recorded twice with different ids: treat a repeated outcome for the
          same <C>data.deployment_id</C> as a duplicate.
        </li>
        <li>
          <strong className="font-medium text-foreground">Ordering</strong> is not guaranteed. <C>seq</C> only increases: keep
          the highest <C>seq</C> seen per app and ignore events with a lower one.
        </li>
        <li>
          <strong className="font-medium text-foreground">Retries.</strong> A non-2xx response, a timeout (10 s) or a connection
          error is retried after about 1m, 5m, 30m, 2h, 6h and 12h (±20% jitter): 7 attempts over roughly 21 hours. Redirects
          are not followed. After the last attempt the delivery is <C>failed</C>; an admin can queue it again with{' '}
          <C>POST /webhook-deliveries/{'{id}'}/redeliver</C>. Deliveries to a deactivated or deleted receiver fail without
          being sent.
        </li>
        <li>
          <strong className="font-medium text-foreground">Scopes.</strong> A <C>global</C> receiver gets events for every app;
          an <C>api_key</C> receiver only for apps created with that key. Apps created with the master key reach global
          receivers only. Deleting an API key deactivates its receivers. This is a delivery filter, not a security boundary:
          any API key without <C>own_apps_only</C> can read all events and receivers.
        </li>
        <li>
          <strong className="font-medium text-foreground">Receiver URLs</strong> must be <C>https</C> without credentials.
          Localhost, private, loopback, link-local (including cloud metadata), carrier-grade NAT and other reserved
          addresses, and numeric host shorthands such as <C>127.1</C>, are rejected, and checked again against the resolved
          address when sending.
        </li>
      </ul>

      <Sub>Catching up</Sub>
      <p>
        <C>GET /events?after_seq=&lt;last seq&gt;</C> lists events oldest first. It returns a contiguous prefix of settled
        events: events from the last ~5 seconds are held back, so a page may hold fewer than <C>limit</C> events, or none,
        while more exist. Pass <C>next_after_seq</C> as the next <C>after_seq</C>; it does not advance on an empty page.{' '}
        <C>ping</C> events are never listed. Events are kept for <C>WEBHOOK_RETENTION_DAYS</C> (14 days by default).
      </p>
      <CodeExample code={catchUpCurl} language="bash" />
    </div>
  );
}
